import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { WarningMessage } from '@common/components';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '../ui/input';
import { LangContext } from '@/context/LangProvider';
import { Button } from '../ui/button';
import { useMutateDeleteOrganization } from '@/reactQuery/post';
import { useGetOrganizations } from '@/reactQuery/get';
import { useUser } from '@/hooks/useUser';
import { useOrganizationDeletionWarnings } from '@/hooks';
import Routes from '@/router/routes';
import { updateOrgWorkspaceCookies } from '@/utils/cookieHelpers';

interface DeleteOrganizationModalProps {
  currentOrgName: string;
}

interface DeleteProps {
  name: string;
}

const defaultValues = {
  name: '',
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Organization name is required'),
});

export function DeleteOrganizationModal({ currentOrgName }: DeleteOrganizationModalProps) {
  const { lang } = useContext(LangContext);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues, resolver: yupResolver(validationSchema) });

  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const { organization_id: organizationId } = useParams();
  const { mutateAsync: mutateAsyncDeleteOrganization } = useMutateDeleteOrganization(
    organizationId!,
  );
  const { user, refetch: refetchUser } = useUser();
  const { data: { items: organizations } = {} } = useGetOrganizations();
  const ownedOrganizations = organizations?.filter((org) => org.owner_id === user?._id);
  const isLastOrganization = !!(ownedOrganizations && ownedOrganizations.length === 1);

  const warningMessage = useOrganizationDeletionWarnings({ isLastOrganization });

  const onSubmit: SubmitHandler<DeleteProps> = async (data) => {
    if (data?.name !== currentOrgName) {
      toast.error(lang.get('msg.invalidOrganizationName'));
      return;
    }

    try {
      await mutateAsyncDeleteOrganization({});
      const result = await refetchUser();
      const refetchedUser = result?.data;

      if (refetchedUser?.last_organization && refetchedUser?.last_workspace) {
        updateOrgWorkspaceCookies(refetchedUser.last_organization, refetchedUser.last_workspace);

        // Close modal and navigate to the new organization
        setOpenModal(false);
        navigate(
          `${Routes.organization.path}/${refetchedUser.last_organization}${Routes.workspace.path}/${refetchedUser.last_workspace}`,
        );
        toast.success(lang.get('msg.organizationSuccessfullyDeleted'));
      }
    } catch {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    }
  };

  return (
    <AlertDialog open={openModal} onOpenChange={setOpenModal}>
      <AlertDialogTrigger asChild>
        <p className="py-1 pl-2 text-sm text-red-600 cursor-pointer hover:bg-secondary-background">
          {lang.get('msg.delete')}
        </p>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-1">
              {lang.get('msg.deleteOrganization')}
            </AlertDialogTitle>
            <p className="text-sm text-dark-text">
              {lang.get('msg.areYouSureYouWantToDeleteOrganization')}
              <br />
              {lang.get('msg.deleteOrganizationModalDescription')}
              <span className="font-bold"> {currentOrgName} </span>
              {lang.get('msg.organization').toLowerCase()}.
            </p>
            {warningMessage && <WarningMessage>{warningMessage}</WarningMessage>}
            <div className="flex flex-col pt-4 pb-12">
              <label className="relative flex flex-col w-full gap-1">
                <p className="mb-2 text-sm text-dark-text">
                  {lang.get('msg.toProceedPleaseEnterOrganizationName')}
                </p>
                <Input
                  {...register('name')}
                  placeholder={lang.get('msg.enterOrganizationName')}
                  className={`w-full ${errors?.name?.message && 'border-red-600/50'}`}
                />
                <p
                  className={`text-xs md700:hidden font-medium text-red-600 opacity-0 absolute -bottom-5 ${
                    errors?.name?.message && 'opacity-100'
                  }`}
                >
                  {errors?.name?.message}
                </p>
              </label>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setOpenModal(false)}>
              {lang.get('msg.cancel')}
            </AlertDialogCancel>
            <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={isSubmitting}>
              {lang.get('msg.submit')}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
